import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertTriangle, Eye, FileText, Rocket, ClipboardCheck, History, Share2, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { renderBody } from '../lib/magazineApi';
import { authFetch } from '../lib/auth';

async function api(path, options = {}) {
  const res = await authFetch(path, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data?.detail?.message || data?.detail || `HTTP ${res.status}`), { status: res.status });
  return data;
}

/* Quality-control checklist shown after every publish. */
function PublishChecklist({ report, result }) {
  const n = report?.normalized || {};
  const live = result?.mode === 'live';
  const has = (v) => (Array.isArray(v) ? v.length > 0 : !!v);
  const items = [
    ['Valid JSON', report?.ok],
    ['Imagen (IA o URL)', has(n.imageUrl) || result?.image_generated],
    ['SEO (title + meta)', has(n.seo?.title) && has(n.seo?.description)],
    ['FAQ', has(n.faq)],
    ['Schema', has(n.seo?.schema_type)],
    ['Internal links', typeof n.content === 'string' && (n.content.includes('](/') || n.content.includes('kitxens.com'))],
    ['CTA', has(n.cta)],
    ['RSS actualizado', live],
    ['Sitemap actualizado', live],
    ['Distribution disponible', live && has(result?.distribution)],
    ['Social Package', has(n.social) || has(result?.distribution?.social)],
    ['Intelligence Ticker', has(result?.intelligence_item) || has(n.intelligence_snippet)],
  ];
  const passed = items.filter(([, ok]) => ok).length;
  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="publish-checklist">
      <p className="text-[11px] uppercase tracking-wider text-[#FF9B4A] font-bold">
        ✔ Publish checklist · {passed}/{items.length}
      </p>
      <div className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {items.map(([label, ok]) => (
          <p key={label} className={`text-[12.5px] ${ok ? 'text-emerald-300' : 'text-amber-300'}`}>
            {ok ? '✅' : '⚠️'} {label}
          </p>
        ))}
      </div>
      {!live && <p className="mt-2 text-[11.5px] text-neutral-500">RSS, sitemap and distribution activate on Publish Live.</p>}
    </div>
  );
}

const LEVEL_UI = {
  pass: { Icon: CheckCircle2, cls: 'text-emerald-400' },
  warn: { Icon: AlertTriangle, cls: 'text-amber-400' },
  error: { Icon: XCircle, cls: 'text-red-400' },
};

const EVENT_LABEL = {
  draft_created: 'Draft created',
  validated: 'Validado',
  published: 'Published',
  republished: 'Re-published',
  image_generated: 'Imagen generada',
  rss_updated: 'RSS actualizado',
  sitemap_updated: 'Sitemap actualizado',
  distributed: 'Distribuido (dlvr.it)',
};

export default function AdminEditorPage() {
  const [text, setText] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [genImage, setGenImage] = useState(true);
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState('');
  const [log, setLog] = useState([]);
  const [articles, setArticles] = useState([]);
  const [inbox, setInbox] = useState(null);
  const [partnerClicks, setPartnerClicks] = useState(null);
  useEffect(() => {
    api('/api/engagement/partner-clicks?days=30').then(setPartnerClicks).catch(() => {});
  }, []);

  const loadLog = useCallback(async (slug) => {
    try {
      const d = await api(`/editorial/log${slug ? `?slug=${slug}` : ''}`);
      setLog(d.events || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  const loadArticles = useCallback(async () => {
    try {
      const d = await api('/articles/manage');
      setArticles(d.articles || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  const loadInbox = useCallback(async () => {
    try { setInbox(await api('/publish/inbox')); } catch { /* silent */ }
  }, []);
  useEffect(() => { loadInbox(); }, [loadInbox]);
  // Fully-live editor: articles, Sonny inbox and history refresh in the background.
  useEffect(() => {
    const t = setInterval(() => { loadArticles(); loadInbox(); loadLog(); }, 15000);
    return () => clearInterval(t);
  }, [loadArticles, loadInbox, loadLog]);
  useEffect(() => {
    if (!inbox?.pending) return undefined;
    const t = setInterval(() => { loadInbox(); loadLog(); loadArticles(); }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inbox?.pending]);

  const sendToSonny = async () => {
    if (!text.trim()) { toast.error('Paste the Publication Package'); return; }
    setBusy('sonny');
    try {
      await api('/publish/inbox', { method: 'POST', body: JSON.stringify({ raw_text: text }) });
      toast.success('📥 En el buzón — Sonny lo publicará automáticamente (~2 min)');
      setText(''); setReport(null); setResult(null);
      await loadInbox();
    } catch (e) { toast.error(String(e.message)); } finally { setBusy(''); }
  };

  const deleteArticle = async (slug) => {
    if (!window.confirm(`¿Eliminar definitivamente "${slug}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api(`/articles/${encodeURIComponent(slug)}`, { method: 'DELETE' });
      toast.success(`Artículo eliminado: ${slug}`);
      await loadArticles();
      await loadLog();
    } catch (e) { toast.error(String(e.message)); }
  };

  const changeCover = async (slug, current) => {
    const url = window.prompt('Nueva imagen de portada (/api/media/... o https://...):', current || '');
    if (!url || url === current) return;
    try {
      await api(`/articles/${encodeURIComponent(slug)}/cover`, { method: 'PUT', body: JSON.stringify({ url }) });
      toast.success('Portada actualizada');
      await loadArticles();
    } catch (e) { toast.error(String(e.message)); }
  };

  const validate = async () => {
    if (!text.trim()) { toast.error('Paste the Publication Package'); return null; }
    setBusy('validate'); setResult(null);
    try {
      const r = await api('/publish/validate', { method: 'POST', body: JSON.stringify({ raw_text: text }) });
      setReport(r);
      toast[r.ok ? 'success' : 'error'](r.ok ? 'Package valid ✅' : 'The package has errors');
      return r;
    } catch (e) {
      if (e.status === 401) { window.location.href = '/login?next=%2Fadmin%2Feditor'; return null; }
      setReport(null);
      toast.error(String(e.message));
      return null;
    } finally { setBusy(''); }
  };

  const openPreview = async () => {
    if (!report) {
      const r = await validate();
      if (!r?.normalized) { toast.error('No se pudo generar el preview — revisa el paquete'); return; }
    }
    setShowPreview(true);
  };

  const refreshAll = async (slug) => { await Promise.all([loadLog(slug), loadArticles(), loadInbox()]); };

  // Double-check: every live publish goes through a confirmation summary first.
  const [confirmFor, setConfirmFor] = useState(null);
  const requestPublish = async (mode) => {
    if (!text.trim()) { toast.error('Paste the Publication Package'); return; }
    let rep = report;
    if (!rep) rep = await validate();
    if (!rep) return;
    if (!rep.ok && mode === 'live' && !overwrite) { toast.error('El paquete tiene errores — corrígelos o marca "Sobrescribir"'); return; }
    setConfirmFor(mode);
  };

  const doPublish = async (mode) => {
    setConfirmFor(null);
    setBusy(mode); setResult(null);
    try {
      const r = await api(mode === 'draft' ? '/penny/draft' : '/penny/publish', {
        method: 'POST',
        body: JSON.stringify({ raw_text: text, overwrite: mode === 'direct' ? true : overwrite, generate_image: genImage }),
      });
      setResult(r);
      toast.success(mode === 'draft' ? 'Draft saved (out of RSS) 📝' : 'Published live! 🚀');
      await refreshAll(r.slug);
    } catch (e) {
      if (e.status === 401) { window.location.href = '/login?next=%2Fadmin%2Feditor'; return; }
      setResult({ ok: false, error: String(e.message) });
      toast.error(String(e.message));
    } finally { setBusy(''); }
  };

  const publish = async (mode) => {
    if (mode === 'draft') {
      if (!text.trim()) { toast.error('Paste the Publication Package'); return; }
      let rep = report;
      if (!rep) rep = await validate();
      if (!rep) return;
      await doPublish('draft');
      return;
    }
    await requestPublish(mode);
  };

  const publishDirect = async () => { await requestPublish('direct'); };

  const previewBlocks = report?.normalized?.content ? renderBody(report.normalized.content) : [];

  return (
    <div className="pt-32 pb-24 min-h-screen bg-neutral-950 text-white" data-testid="admin-editor-page">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="kx-eyebrow mb-2">EDITORIAL INBOX</p>
            <h1 className="text-3xl font-bold">Penny → Emergent → Knowledge Center</h1>
            <p className="mt-2 text-sm text-neutral-400">Paste the Publication Package — or send it to Sonny's inbox and he publishes it for you. RSS, sitemap and dlvr.it distribution update automatically.</p>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Left: paste + actions */}
          <div>
            <textarea value={text} onChange={(e) => { setText(e.target.value); setReport(null); setResult(null); }}
              rows={16} placeholder={'Paste Penny\u2019s full output here…\n(with or without "READY FOR EMERGENT")'}
              className="w-full rounded-2xl bg-neutral-900 border border-neutral-800 focus:border-[#F77F11] outline-none p-5 text-[13px] font-mono text-neutral-200 resize-y"
              data-testid="editor-textarea" />
            <Button onClick={publishDirect} disabled={!!busy}
              className="mt-3 w-full bg-[#F77F11] hover:bg-[#d8651a] rounded-full h-12 text-base font-bold shadow-lg shadow-[#F77F11]/20" data-testid="editor-direct-publish-btn">
              <Rocket className="w-5 h-5 mr-2" /> {busy === 'direct' ? 'Publishing…' : 'Paste & Publish'}
            </Button>
            <p className="mt-1.5 text-[11.5px] text-neutral-500 text-center">Auto-repara el JSON, valida y te muestra el doble check antes de salir en vivo (sobrescribe si el artículo ya existe).</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button onClick={validate} disabled={busy === 'validate'}
                className="bg-white text-neutral-900 hover:bg-neutral-200 rounded-full h-11 px-5 font-semibold" data-testid="editor-validate-btn">
                <ClipboardCheck className="w-4 h-4 mr-1.5" /> {busy === 'validate' ? 'Validando…' : 'Validar'}
              </Button>
              <Button onClick={openPreview} disabled={!text.trim() || !!busy}
                variant="outline" className="border-neutral-700 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-full h-11 px-5 font-semibold" data-testid="editor-preview-btn">
                <Eye className="w-4 h-4 mr-1.5" /> Preview
              </Button>
              <Button onClick={() => publish('draft')} disabled={!text.trim() || !!busy}
                variant="outline" className="border-neutral-600 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-full h-11 px-5 font-semibold" data-testid="editor-draft-btn">
                <FileText className="w-4 h-4 mr-1.5" /> {busy === 'draft' ? 'Saving…' : 'Publish Draft'}
              </Button>
              <Button onClick={() => publish('live')} disabled={!text.trim() || !!busy}
                variant="outline" className="border-[#F77F11]/50 bg-transparent text-[#ffb374] hover:bg-[#F77F11]/10 hover:text-[#ffb374] rounded-full h-11 px-6 font-semibold" data-testid="editor-live-btn">
                <Rocket className="w-4 h-4 mr-1.5" /> {busy === 'live' ? 'Publishing…' : 'Publish Live'}
              </Button>
              <Button onClick={sendToSonny} disabled={!text.trim() || !!busy}
                variant="outline" className="border-emerald-500/50 bg-transparent text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-300 rounded-full h-11 px-5 font-semibold" data-testid="editor-sonny-btn">
                🤖 {busy === 'sonny' ? 'Enviando…' : 'Buzón de Sonny'}
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-5 text-sm text-neutral-300">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="accent-[#F77F11]" />
                Sobrescribir si el slug ya existe
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={genImage} onChange={(e) => setGenImage(e.target.checked)} className="accent-[#F77F11]" />
                Generar portada con IA (si no hay imagen)
              </label>
            </div>

            {/* Result */}
            {result && (
              <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${result.ok ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-red-500/40 bg-red-500/10 text-red-300'}`} data-testid="editor-result">
                {result.ok ? (
                  <div className="space-y-1">
                    <p>✅ {result.mode === 'draft' ? 'Draft saved' : (result.action === 'created' ? 'Published' : 'Updated')} · ID <code className="text-[12px]">{result.article_id}</code> · status <b>{result.lifecycle_status}</b></p>
                    {result.url && <p>🔗 <a href={result.url} target="_blank" rel="noreferrer" className="underline font-semibold">{result.url}</a></p>}
                    {result.image_generated && <p>🖼️ Portada generada con IA</p>}
                    {result.mode === 'live' && <p>📡 RSS ✓ · Sitemap ✓ · Webhook ✓ → dlvr.it distribuirá automáticamente</p>}
                    {result.preview_note && <p className="text-amber-300">📝 {result.preview_note}</p>}
                    {result.warnings?.map((w, i) => <p key={i} className="text-amber-300">⚠️ {w}</p>)}
                  </div>
                ) : (<>❌ {result.error}</>)}
              </div>
            )}
            {result?.ok && report && <PublishChecklist report={report} result={result} />}
          </div>

          {/* Right: last published + validation report + log */}
          <div>
            {(() => {
              const pubs = articles.filter((a) => a.status !== 'draft')
                .slice().sort((a, b) => String(b.published_at || '').localeCompare(String(a.published_at || '')));
              const lastPub = pubs[0];
              if (!lastPub) return null;
              const justNow = result?.ok && result?.slug === lastPub.slug;
              return (
                <div className={`mb-6 rounded-2xl border p-5 ${justNow ? 'border-[#F77F11] bg-[#F77F11]/[0.07]' : 'border-neutral-800 bg-neutral-900/70'}`} data-testid="editor-last-published">
                  <p className="text-[11px] uppercase tracking-wider font-bold flex items-center gap-2" style={{ color: '#FF9B4A' }}>
                    <Rocket className="w-3.5 h-3.5" /> {justNow ? 'Publicado ahora mismo' : 'Último publicado en la webzine'}
                  </p>
                  <div className="mt-3 flex items-center gap-4">
                    {lastPub.cover_image
                      ? <img src={lastPub.cover_image} alt="" className="w-24 h-16 object-cover rounded-lg border border-neutral-800 shrink-0" />
                      : <span className="w-24 h-16 rounded-lg bg-neutral-800 shrink-0" />}
                    <div className="min-w-0">
                      <a href={`/magazine/${lastPub.slug}`} target="_blank" rel="noopener noreferrer"
                        className="block text-[15px] font-bold text-white hover:text-[#FF9B4A] transition-colors leading-snug" data-testid="editor-last-published-link">
                        {lastPub.title}
                      </a>
                      <p className="mt-1 text-[11.5px] text-neutral-500">{lastPub.category} · {(lastPub.published_at || '').replace('T', ' ').slice(0, 16)}</p>
                      <p className="mt-1.5 flex flex-wrap gap-2 text-[10px] font-bold uppercase">
                        <span className="rounded-full px-2 py-0.5 bg-emerald-500/15 text-emerald-300">Live</span>
                        <span className="rounded-full px-2 py-0.5 bg-emerald-500/15 text-emerald-300">RSS ✓</span>
                        <span className="rounded-full px-2 py-0.5 bg-emerald-500/15 text-emerald-300">Sitemap ✓</span>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
            {partnerClicks && partnerClicks.total > 0 && (
              <div className="mb-6 rounded-2xl border border-neutral-800 bg-neutral-900/70 p-5" data-testid="editor-partner-clicks">
                <p className="text-[11px] uppercase tracking-wider font-bold text-neutral-400 flex items-center gap-2">
                  🔗 Clicks de partner (Marblism) · 30 días
                  <span className="ml-auto rounded-full bg-[#F77F11]/15 text-[#FF9B4A] px-2 py-0.5 text-[11px]">{partnerClicks.total}</span>
                </p>
                <ul className="mt-3 space-y-1.5">
                  {partnerClicks.pages.slice(0, 5).map((p) => (
                    <li key={p.page} className="flex items-center gap-2 text-[12.5px]" data-testid={`partner-click-row-${p.page}`}>
                      <span className="truncate text-neutral-300">{p.page}</span>
                      <span className="ml-auto font-bold text-white shrink-0">{p.clicks}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="rounded-2xl border border-neutral-800 bg-neutral-900/70 p-6" data-testid="validation-report">
              <p className="font-semibold mb-4 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-[#F77F11]" /> Validation report</p>
              {!report ? (
                <p className="text-sm text-neutral-500">Paste a package and hit Validate.</p>
              ) : (
                <ul className="space-y-2.5">
                  {report.checks.map((c) => {
                    const { Icon, cls } = LEVEL_UI[c.level];
                    return (
                      <li key={c.id} className="flex items-start gap-2.5 text-[13.5px]">
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cls}`} />
                        <span className="text-neutral-300 min-w-0">{c.message}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-6 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-6" data-testid="sonny-inbox-card">
              <p className="font-semibold mb-1 flex items-center gap-2">🤖 Buzón de Sonny · auto-publicación</p>
              <p className="text-[12px] text-neutral-500 mb-3">Solo muestra lo que entra POR EL BUZÓN (las publicaciones directas aparecen arriba en "Último publicado"). Todo se valida y publica solo; queda en el historial y en el Event Bus.</p>
              {!inbox?.items?.length ? (
                <p className="text-sm text-neutral-500">Buzón vacío — usa el botón "🤖 Buzón de Sonny".</p>
              ) : (
                <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {inbox.items.map((it) => (
                    <li key={it._id} className="flex items-center justify-between gap-3 text-[12.5px] border-b border-neutral-800/70 pb-2" data-testid={`sonny-inbox-item-${it._id}`}>
                      <span className="min-w-0 truncate text-neutral-300">
                        {it.status === 'published' && it.url
                          ? <a href={it.url} target="_blank" rel="noreferrer" className="underline text-emerald-300 font-semibold">{it.slug || it.url}</a>
                          : <span>{it.slug || it._id.slice(0, 8)}{it.error ? ` — ${it.error.slice(0, 80)}` : ''}</span>}
                      </span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${it.status === 'published' ? 'bg-emerald-500/15 text-emerald-300' : it.status === 'error' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>
                        {it.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900/70 p-6" data-testid="editorial-log">
              <p className="font-semibold mb-4 flex items-center gap-2"><History className="w-4 h-4 text-[#F77F11]" /> Historial editorial</p>
              {log.length === 0 ? (
                <p className="text-sm text-neutral-500">No events yet.</p>
              ) : (
                <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {log.map((e, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 text-[12.5px] border-b border-neutral-800/70 pb-2">
                      <span className="text-neutral-300 min-w-0 truncate"><b className="text-[#ffb374]">{EVENT_LABEL[e.event] || e.event}</b> · {e.slug}</span>
                      <span className="text-neutral-500 shrink-0">{new Date(e.at).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Published articles manager */}
        <div className="mt-8 rounded-2xl border border-neutral-800 bg-neutral-900/60 p-5" data-testid="editor-articles-manager">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] uppercase tracking-wider text-[#FF9B4A] font-bold">
              📚 Artículos publicados · {articles.length}
            </p>
            <Button onClick={loadArticles} variant="outline"
              className="border-neutral-700 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-full h-8 px-4 text-[12px]" data-testid="editor-articles-refresh">
              Refrescar
            </Button>
          </div>
          <ul className="mt-3 divide-y divide-neutral-800/70">
            {articles.map((a) => (
              <li key={a.slug} className="py-2.5 flex flex-wrap items-center gap-3" data-testid={`editor-article-row-${a.slug}`}>
                {a.cover_image
                  ? <img src={a.cover_image} alt="" className="w-14 h-9 object-cover rounded-md border border-neutral-800 shrink-0" />
                  : <span className="w-14 h-9 rounded-md bg-neutral-800 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <a href={`/magazine/${a.slug}`} target="_blank" rel="noopener noreferrer"
                    className="block text-[13px] font-semibold text-neutral-200 hover:text-[#FF9B4A] truncate transition-colors">{a.title}</a>
                  <p className="text-[11px] text-neutral-500 truncate">{a.category} · {a.slug} · {(a.published_at || '').slice(0, 10)}{a.status === 'draft' ? ' · DRAFT' : ''}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" onClick={() => changeCover(a.slug, a.cover_image)}
                    className="text-[11.5px] font-bold text-neutral-400 hover:text-white border border-neutral-700 hover:border-neutral-500 rounded-full px-3 py-1.5 transition-colors"
                    data-testid={`editor-article-cover-${a.slug}`}>
                    🖼 Imagen
                  </button>
                  <button type="button" onClick={() => deleteArticle(a.slug)}
                    className="inline-flex items-center gap-1 text-[11.5px] font-bold text-red-400 hover:text-white hover:bg-red-500/90 border border-red-500/40 rounded-full px-3 py-1.5 transition-colors"
                    data-testid={`editor-article-delete-${a.slug}`}>
                    <Trash2 className="w-3 h-3" /> Eliminar
                  </button>
                </div>
              </li>
            ))}
            {articles.length === 0 && <li className="py-3 text-[12.5px] text-neutral-500">Sin artículos.</li>}
          </ul>
        </div>

        {/* Preview — a real webzine window, not a buried pane */}
        {showPreview && report?.normalized && (
          <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" onClick={() => setShowPreview(false)} data-testid="editor-preview-modal">
            <div className="relative w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-3xl bg-white text-neutral-900 p-8 sm:p-12 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="editor-preview-pane">
              <button onClick={() => setShowPreview(false)} data-testid="editor-preview-close"
                className="sticky top-0 float-right -mt-4 -mr-4 w-9 h-9 rounded-full bg-neutral-900 text-white flex items-center justify-center hover:bg-[#F77F11] transition-colors z-10" aria-label="Close preview">
                <X className="w-4 h-4" />
              </button>
              <p className="kx-eyebrow mb-3">PREVIEW · {report.normalized.category}</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{report.normalized.title}</h2>
            {report.normalized.excerpt && <p className="mt-4 text-lg text-neutral-600">{report.normalized.excerpt}</p>}
            <div className="mt-8 prose max-w-none">
              {previewBlocks.map((b, i) => {
                if (b.type === 'h2') return <h3 key={i} className="text-2xl font-bold mt-8 mb-3">{b.text}</h3>;
                if (b.type === 'h3') return <h4 key={i} className="text-xl font-bold mt-6 mb-2">{b.text}</h4>;
                if (b.type === 'ul') return <ul key={i} className="list-disc pl-6 space-y-1 my-3">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
                if (b.type === 'ol') return <ol key={i} className="list-decimal pl-6 space-y-1 my-3">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ol>;
                if (b.type === 'hr') return <hr key={i} className="my-6 border-neutral-200" />;
                return <p key={i} className="my-3 leading-relaxed text-neutral-700">{b.lead && <strong className="block mb-1 text-neutral-900">{b.lead}</strong>}{b.text}</p>;
              })}
            </div>
            {report.normalized.faq?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-neutral-200">
                <h3 className="text-xl font-bold mb-3">FAQ</h3>
                {report.normalized.faq.map((f, i) => (
                  <div key={i} className="mb-3"><p className="font-semibold">{f.question}</p><p className="text-neutral-600 text-sm">{f.answer}</p></div>
                ))}
              </div>
            )}
            </div>
          </div>
        )}

        {/* Double-check before anything goes live */}
        {confirmFor && report?.normalized && (
          <div className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmFor(null)} data-testid="editor-confirm-modal">
            <div className="w-full max-w-lg rounded-2xl bg-neutral-900 border border-neutral-700 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <p className="text-[17px] font-bold flex items-center gap-2"><Rocket className="w-4 h-4 text-[#F77F11]" /> Doble check antes de publicar</p>
              <div className="mt-4 space-y-2 text-[13px]">
                <p className="text-white font-semibold text-[15px] leading-snug">{report.normalized.title}</p>
                <p className="text-neutral-400">/magazine/<b className="text-neutral-200">{report.normalized.slug}</b> · {report.normalized.category}</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {[
                    [report.ok, report.ok ? 'Validación OK' : 'Con errores (sobrescribir activo)'],
                    [!!(report.normalized.cover_image || report.normalized.image), (report.normalized.cover_image || report.normalized.image) ? 'Portada incluida' : (genImage ? 'Portada: se genera con IA' : 'SIN portada')],
                    [true, 'Knowledge Center'], [true, 'RSS'], [true, 'Sitemap'], [true, 'dlvr.it → redes'],
                  ].map(([ok, label], i) => (
                    <span key={i} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase ${ok ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {label}
                    </span>
                  ))}
                </div>
                {report.checks?.filter((c) => c.level !== 'ok').slice(0, 4).map((c) => (
                  <p key={c.id} className="text-[12px] text-amber-300 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {c.message}</p>
                ))}
                <p className="pt-1 text-[11.5px] text-neutral-500">Al confirmar: se publica en la webzine, se actualizan RSS y sitemap, y dlvr.it distribuye a redes automáticamente.</p>
                <details className="pt-1" data-testid="editor-social-previews">
                  <summary className="cursor-pointer text-[12px] font-bold text-[#FF9B4A] hover:opacity-80">Ver textos que Sonny enviará a redes (LinkedIn · X · Instagram)</summary>
                  <div className="mt-2 space-y-2">
                    {(() => {
                      const n = report.normalized;
                      const u = (s) => `https://kitxens.com/magazine/${n.slug}?utm_source=${s}&utm_medium=social&utm_campaign=kc_${n.slug}`;
                      return [
                        ['LinkedIn', `${n.title}\n\n${n.excerpt || ''}\n\n${u('linkedin')}\n\n#Restaurants #AI #KITXENS`],
                        ['X', `${String(n.title || '').slice(0, 180)} → ${u('x')}`],
                        ['Instagram', `${n.title}\n\n${n.excerpt || ''}\n\nLee el artículo completo → ${u('instagram')}\n\n#restaurants #AI #KITXENS`],
                      ].map(([net, txt]) => (
                        <div key={net} className="rounded-lg bg-neutral-800/80 border border-neutral-700 p-2.5" data-testid={`editor-social-preview-${net.toLowerCase()}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-400">{net}</p>
                            <button type="button" onClick={() => { navigator.clipboard?.writeText(txt); toast.success(`Texto de ${net} copiado`); }}
                              className="text-[10px] font-bold text-[#FF9B4A] hover:opacity-80" data-testid={`editor-social-copy-${net.toLowerCase()}`}>Copiar</button>
                          </div>
                          <p className="mt-1 text-[11px] text-neutral-300 whitespace-pre-wrap leading-snug">{txt}</p>
                        </div>
                      ));
                    })()}
                    <p className="text-[10.5px] text-neutral-500">dlvr.it publica automáticamente título + enlace con UTM; estos textos están listos para pegar si quieres personalizar un post a mano.</p>
                  </div>
                </details>
              </div>
              <div className="mt-5 flex gap-3">
                <Button onClick={() => setConfirmFor(null)} variant="outline"
                  className="flex-1 border-neutral-600 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-full h-11 font-semibold" data-testid="editor-confirm-cancel">
                  Revisar más
                </Button>
                <Button onClick={() => doPublish(confirmFor)} disabled={!!busy}
                  className="flex-1 bg-[#F77F11] hover:bg-[#d8651a] rounded-full h-11 font-bold" data-testid="editor-confirm-publish">
                  <Rocket className="w-4 h-4 mr-1.5" /> Confirmar y publicar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
